/* eslint-disable @typescript-eslint/no-explicit-any */
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import moment from "moment";

interface Nfe {
  serie: number;
  nfe: number;
  dhemi: string;
  nome: string;
  cpf: string;
  cfop: string | null;
  vnfe: number;
  vprod?: number;
  finalidade: string;
  status: string;
}

// comSemImpostos: coluna e total extra com a soma só dos itens (vprod), além
// do total da nota (vnfe, com impostos/frete/etc.).
async function RelatorioNfe(dados: Nfe[], periodo: string, comSemImpostos = false) {

  const totalGeral = dados.reduce(
    (acc, item) => acc + Number(item?.vnfe ?? 0),
    0
  );
  const totalItens = dados.reduce(
    (acc, item) => acc + Number(item?.vprod ?? 0),
    0
  );

  const linhas: any[] = [];

  dados.forEach((r) => {
    linhas.push([
      {
        text: moment(r.dhemi).format("DD/MM/YYYY"),
        fontSize: 8
      },
      {
        text: String(r.serie),
        fontSize: 8,
        alignment: "left"
      },
      {
        text: String(r.nfe),
        fontSize: 8,
        alignment: "left"
      },
      {
        text: r.nome,
        fontSize: 8
      },
      {
        text: r.cpf,
        fontSize: 8
      },
      {
        text: r.finalidade ?? "",
        fontSize: 8
      },
      {
        text: r.status,
        fontSize: 8,
        alignment: "left",
        color: (() => {
          switch (r.status) {
            case "Cancelada":
              return "red";
            case "Lançada":
              return "#1976d2";
            case "Autorizada":
              return "green";
            default:
              return "black";
          }
        })()
      },
      ...(comSemImpostos ? [{
        text: formatMoney(Number(r.vprod ?? 0)),
        alignment: "right",
        fontSize: 8
      }] : []),
      {
        text: formatMoney(r.vnfe),
        alignment: "right",
        bold: true,
        fontSize: 8
      }
    ]);
    // Devolução marcada (fundo laranja claro) — no relatório "Normal + Devolução"
    // dá pra ver quais são sem olhar a coluna Finalidade.
    if (r.finalidade === "Devolução") {
      linhas[linhas.length - 1] = linhas[linhas.length - 1].map((celula: any) => ({ ...celula, fillColor: "#ffedd5" }));
    }
  });

  const devolucoes = dados.filter((r) => r.finalidade === "Devolução");
  const totalDevolucao = devolucoes.reduce((acc, item) => acc + Number(item?.vnfe ?? 0), 0);
  const temNormalEDevolucao = devolucoes.length > 0 && devolucoes.length < dados.length;

  const documentDefinition: any = {
    pageSize: "A4",
    pageMargins: [25, 25, 25, 25],

    content: [

      {
        text: process.env.REACT_APP_URL_EMPRESA,
        fontSize: 14,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 4]
      },

      {
        text: "RELATÓRIO DE NOTAS FISCAIS (NF-e)",
        fontSize: 12,
        bold: true,
        alignment: "center"
      },

      {
        text: periodo,
        alignment: "center",
        fontSize: 9,
        margin: [0, 0, 0, 10]
      },

      {
        table: {
          headerRows: 1,
          widths: comSemImpostos ? [50, 25, 30, "*", 70, 50, 45, 50, 50] : [55, 30, 30, "*", 70, 50, 50, 50],

          body: [

            [
              { text: "Data", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "Série", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "NF-e", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "Cliente", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "CPF/CNPJ", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "Finalidade", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              { text: "Status", bold: true, fillColor: "#e5e7eb", fontSize: 8 },
              ...(comSemImpostos ? [{ text: "Itens (s/ imp.)", bold: true, alignment: "right", fillColor: "#e5e7eb", fontSize: 8 }] : []),
              { text: comSemImpostos ? "Total (c/ imp.)" : "Valor", bold: true, alignment: "right", fillColor: "#e5e7eb", fontSize: 8 }
            ],

            ...linhas
          ]
        },

        layout: {

          fillColor: function (rowIndex: number) {
            if (rowIndex === 0) return null;
            return rowIndex % 2 === 0 ? "#f9fafb" : null;
          },

          hLineColor: () => "#d1d5db",
          vLineWidth: () => 0
        }
      },

      ...(temNormalEDevolucao ? [
        {
          text: `Notas normais (${dados.length - devolucoes.length}): ` + formatMoney(totalGeral - totalDevolucao),
          alignment: "right",
          fontSize: 9,
          margin: [0, 15, 0, 0]
        },
        {
          text: `Notas de devolução (${devolucoes.length}) — marcadas em laranja: ` + formatMoney(totalDevolucao),
          alignment: "right",
          fontSize: 9,
          color: "#c2410c",
          margin: [0, 2, 0, 0]
        }
      ] : []),
      {
        text: (comSemImpostos ? "TOTAL COM IMPOSTOS: " : "TOTAL GERAL: ") + formatMoney(totalGeral),
        alignment: "right",
        bold: true,
        fontSize: 11,
        margin: [0, 15, 0, 0]
      },
      ...(comSemImpostos ? [
        {
          text: "TOTAL SEM IMPOSTOS (soma dos itens): " + formatMoney(totalItens),
          alignment: "right",
          bold: true,
          fontSize: 11,
          margin: [0, 4, 0, 0]
        },
        {
          text: "Diferença (impostos, frete, outras despesas e descontos): " + formatMoney(totalGeral - totalItens),
          alignment: "right",
          fontSize: 9,
          margin: [0, 2, 0, 0]
        }
      ] : [])
    ]
  };

  pdfMake.createPdf(documentDefinition).open();
}

function formatMoney(value: number) {
  const v = Number(value ?? 0);

  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(v.toFixed(2)));
}

export default RelatorioNfe;